import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getTiptapPortalMountPoint, PopupPosition } from '../../tiptapPopup';
import { createPortal } from 'react-dom';
import { CANVAS_VIEWPORT_ID } from '../../../../canvas/utils/canvasConstants';
import BoardLinkPopupContent from './BoardLinkPopupContent';
import { Popover } from '@milanote/roma';
import { defer } from 'lodash';

export type SelectedBoard = {
    boardId: string;
    permissionId?: string;
    title: string;
    color?: string;
    url?: string;
};

type BoardLinkPopupProps = {
    position: PopupPosition | null;
    isOpen: boolean;
};

const VIEWPORT_MARGIN_PX = 16;

const BoardLinkPopup: React.FC<BoardLinkPopupProps> = ({ isOpen, position }) => {
    const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });
    const [anchor, setAnchor] = useState(position?.anchor ?? 'bottom');
    const popupRef = useRef<HTMLDivElement>(null);
    const popupHeightRef = useRef(0);
    const scrollOffsetRef = useRef(scrollOffset);
    const positionRef = useRef(position);
    const portalMountPoint = useMemo(() => getTiptapPortalMountPoint(), []);

    useLayoutEffect(() => {
        scrollOffsetRef.current = scrollOffset;
    }, [scrollOffset]);

    useLayoutEffect(() => {
        positionRef.current = position;
    }, [position]);

    useEffect(() => {
        if (!isOpen) return;

        const timeoutId = defer(() => {
            if (popupHeightRef.current !== 0) return;

            setAnchor(position?.anchor ?? 'bottom');
        });

        return () => clearTimeout(timeoutId);
    }, [isOpen, position]);

    const updateAnchor = useCallback(
        (nextScrollOffset = scrollOffsetRef.current) => {
            const currentPosition = positionRef.current;
            if (!isOpen || !currentPosition) return;

            const anchorTop = (currentPosition.anchorRect?.top ?? currentPosition.point.y) + nextScrollOffset.y;
            const anchorBottom = (currentPosition.anchorRect?.bottom ?? currentPosition.point.y) + nextScrollOffset.y;
            const spaceAbove = anchorTop - VIEWPORT_MARGIN_PX;
            const spaceBelow = window.innerHeight - anchorBottom - VIEWPORT_MARGIN_PX;
            const shouldRenderAbove = spaceBelow < popupHeightRef.current && spaceAbove > popupHeightRef.current;
            const nextAnchor = shouldRenderAbove ? 'top' : 'bottom';

            setAnchor(nextAnchor);
        },
        [isOpen],
    );

    useEffect(() => {
        if (!isOpen) return;

        const canvasViewport = document.getElementById(CANVAS_VIEWPORT_ID);
        if (!canvasViewport) return;

        const initialScroll = { x: canvasViewport.scrollLeft, y: canvasViewport.scrollTop };
        const onViewportScroll = () => {
            const currentScroll = { x: canvasViewport.scrollLeft, y: canvasViewport.scrollTop };
            const nextScrollOffset = {
                x: initialScroll.x - currentScroll.x,
                y: initialScroll.y - currentScroll.y,
            };

            scrollOffsetRef.current = nextScrollOffset;
            setScrollOffset(nextScrollOffset);
            updateAnchor(nextScrollOffset);
        };

        canvasViewport.addEventListener('scroll', onViewportScroll);
        return () => canvasViewport.removeEventListener('scroll', onViewportScroll);
    }, [isOpen, updateAnchor]);

    useLayoutEffect(() => {
        if (!isOpen || !popupRef.current) return;

        const popup = popupRef.current;
        const updatePopupHeight = () => {
            popupHeightRef.current = popup.getBoundingClientRect().height;
            updateAnchor();
        };

        updatePopupHeight();
        const resizeObserver = new ResizeObserver(updatePopupHeight);
        resizeObserver.observe(popup);
        window.addEventListener('resize', updateAnchor);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateAnchor);
        };
    }, [isOpen, updateAnchor]);

    useLayoutEffect(() => {
        if (!isOpen || !position) return;

        updateAnchor();
    }, [isOpen, position, updateAnchor]);

    if (!isOpen || !position) return null;

    const anchorTop = (position.anchorRect?.top ?? position.point.y) + scrollOffset.y;
    const anchorBottom = (position.anchorRect?.bottom ?? position.point.y) + scrollOffset.y;

    const verticalPositionStyle =
        anchor === 'bottom' ? { top: anchorBottom } : { bottom: `calc(100% - ${anchorTop}px)` };

    if (!portalMountPoint) return null;

    return createPortal(
        <div
            ref={popupRef}
            className="BoardLinkPopup"
            style={{
                transform: 'scale(1)',
                position: 'fixed',

                left: position.point.x + scrollOffset.x,
                ...verticalPositionStyle,
            }}
            onTouchStart={(e) => e.stopPropagation()}
        >
            <Popover.Base className="BoardLinkPopupSurface flex max-h-96 w-2xs flex-col overflow-hidden">
                <BoardLinkPopupContent />
            </Popover.Base>
        </div>,
        portalMountPoint,
    );
};

export default BoardLinkPopup;
